import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Card from '../../components/base/Card';
import Navbar from '../../components/feature/Navbar';

// Hook per animazione counter
const useCountUp = (end: number, duration: number = 2000, decimals: number = 0) => {
  const [count, setCount] = useState(0);
  
  useEffect(() => {
    let startTime: number;
    let animationFrame: number;
    
    const animate = (currentTime: number) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);
      
      // Easing function per effetto più fluido
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      const currentCount = end * easeOutQuart;
      
      setCount(decimals > 0 ? Number(currentCount.toFixed(decimals)) : Math.floor(currentCount));
      
      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      }
    };
    
    animationFrame = requestAnimationFrame(animate);
    
    return () => {
      if (animationFrame) {
        cancelAnimationFrame(animationFrame);
      }
    };
  }, [end, duration, decimals]);
  
  return count;
};

export default function RisultatiPage() {
  const navigate = useNavigate();
  const [selectedPeriod, setSelectedPeriod] = useState('tutto');
  const [showAllStats, setShowAllStats] = useState(false);
  
  // Update document head for SEO
  useEffect(() => {
    document.title = 'Risultati Verificati Trading - Fortal Society | Performance Reali';
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Risultati di trading verificati e trasparenti di Fortal Society. Performance reali dell\'account TCA BOMBER PUSHED con rendimento totale del 26470.8% e Sharpe Ratio 0.18.');
    }
    
    // Update meta keywords
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'risultati trading, performance trading, trading verificato, TCA BOMBER PUSHED, rendimento trading, Fortal Society risultati');
    }
    
    // Add canonical URL
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = `${import.meta.env.VITE_SITE_URL || 'https://fortalsociety.com'}/risultati`;
    
    // Add Schema.org JSON-LD for results page
    const existingScript = document.querySelector('script[data-schema="results"]');
    if (existingScript) {
      existingScript.remove();
    }
    
    const schema = {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Risultati Verificati Trading",
      "description": "Performance reali e trasparenti dell'account TCA BOMBER PUSHED, verificate e monitorate da FP Markets",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://fortalsociety.com'}/risultati`,
      "mainEntity": {
        "@type": "FinancialProduct",
        "name": "TCA BOMBER PUSHED",
        "description": "Account di trading professionale con performance verificate",
        "provider": {
          "@type": "Organization",
          "name": "Fortal Society",
          "url": `${import.meta.env.VITE_SITE_URL || 'https://fortalsociety.com'}`
        }
      },
      "breadcrumb": {
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": `${import.meta.env.VITE_SITE_URL || 'https://fortalsociety.com'}/`
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Risultati",
            "item": `${import.meta.env.VITE_SITE_URL || 'https://fortalsociety.com'}/risultati`
          }
        ]
      }
    };
    
    const schemaScript = document.createElement('script');
    schemaScript.type = 'application/ld+json';
    schemaScript.setAttribute('data-schema', 'results');
    schemaScript.textContent = JSON.stringify(schema);
    document.head.appendChild(schemaScript);
    
    return () => {
      const script = document.querySelector('script[data-schema="results"]');
      if (script) {
        script.remove();
      }
    };
  }, []);
  
  // Animazioni counter per i numeri principali - DATI AGGIORNATI IN TEMPO REALE
  const totalReturn = useCountUp(26470.8, 3000, 1);
  const activeDays = useCountUp(504, 2500);
  const monthlyAvg = useCountUp(38.87, 2000, 2);
  const sharpeRatio = useCountUp(0.18, 2500, 2);
  const maxProfit = useCountUp(58384.82, 3500, 2);
  const maxDrawdown = useCountUp(79.33, 2000, 2);

  const handleHomeClick = () => {
    navigate('/');
  };

  const performanceData = {
    tutto: {
      rendimento: totalReturn,
      profitto: maxProfit,
      drawdown: maxDrawdown,
      sharpe: sharpeRatio,
      volatility: 0.04,
      recovery: 0.96
    },
    anno: {
      rendimento: 423.22,
      profitto: 58384.82,
      drawdown: 79.33,
      sharpe: 0.18,
      volatility: 0.04,
      recovery: 0.96
    },
    semestre: {
      rendimento: 114.24,
      profitto: 45230.50,
      drawdown: 45.20,
      sharpe: 0.18,
      volatility: 0.04,
      recovery: 0.96
    },
    trimestre: {
      rendimento: 287.66,
      profitto: 52100.30,
      drawdown: 62.15,
      sharpe: 0.18,
      volatility: 0.04,
      recovery: 0.96
    },
    mese: {
      rendimento: 30.16,
      profitto: 18945.30,
      drawdown: 25.67,
      sharpe: 0.18,
      volatility: 0.04,
      recovery: 0.96
    },
    settimana: {
      rendimento: 10.4,
      profitto: 6134.80,
      drawdown: 8.15,
      sharpe: 0.18,
      volatility: 0.04,
      recovery: 0.96
    },
    giorno: {
      rendimento: 3.86,
      profitto: 2156.20,
      drawdown: 1.12,
      sharpe: 0.18,
      volatility: 0.04,
      recovery: 0.96
    }
  };

  const currentData = performanceData[selectedPeriod as keyof typeof performanceData];

  const mainStats = [
    {
      icon: 'ri-line-chart-line',
      label: 'Rendimento Totale',
      value: '38104.4%',
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-50',
    },
    {
      icon: 'ri-calendar-line',
      label: 'Giorni Attivi',
      value: '540',
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
    },
    {
      icon: 'ri-trophy-line',
      label: 'Ranking Globale',
      value: '#15',
      color: 'text-amber-600',
      bgColor: 'bg-amber-50',
    },
    {
      icon: 'ri-bar-chart-line',
      label: 'Media Mensile',
      value: '39.14%',
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
    },
  ];

  const detailedStats = [
    { label: 'Sharpe Ratio', value: '0.18' },
    { label: 'Volatility Ratio', value: '0.03' },
    { label: 'Recovery Factor', value: '1.50' },
    { label: 'Profitto Massimo', value: '81603.17%' },
    { label: 'Max Drawdown', value: '79.33%' },
    { label: 'Media Settimanale', value: '7.92%' },
    { label: 'Deviazione Giornaliera', value: '16.64%' },
    { label: 'Deviazione Mensile', value: '4.4%' },
    { label: 'Deviazione Annuale', value: '10.45%' },
  ];

  return (
    <div className="min-h-screen bg-slate-950 relative overflow-hidden">
      {/* Navbar */}
      <Navbar />

      {/* Particelle animate di sfondo */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 bg-amber-400/30 rounded-full animate-particles"
            style={{
              left: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 15}s`,
              animationDuration: `${15 + Math.random() * 10}s`
            }}
          />
        ))}
      </div>

      {/* Hero Section */}
      <section className="relative pt-20 pb-16 px-4">
        <div className="max-w-7xl mx-auto">
          {/* Pulsante Home */}
          <div className="mb-8 animate-slide-in-left">
            <button
              onClick={handleHomeClick}
              className="group flex items-center gap-3 px-6 py-3 glass-effect rounded-xl hover:glass-gradient transition-all duration-300 hover-lift"
            >
              <div className="w-8 h-8 flex items-center justify-center">
                <i className="ri-arrow-left-line text-xl text-amber-400 group-hover:text-amber-300 transition-colors duration-300"></i>
              </div>
              <span className="text-slate-200 group-hover:text-white font-medium">
                Torna alla Home
              </span>
            </button>
          </div>

          {/* Titolo principale */}
          <div className="text-center mb-16 animate-slide-in-up">
            <h1 className="text-5xl md:text-7xl font-bold text-gradient-gold mb-6 animate-bounce-in">
              Risultati Verificati
            </h1>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto animate-slide-in-up delay-200">
              Performance reali e trasparenti del nostro account TCA BOMBER PUSHED, 
              verificate e monitorate da FP Markets
            </p>
          </div>

          {/* Metriche principali animate */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            <Card className="glass-gradient p-6 text-center hover-lift animate-bounce-in delay-100">
              <div className="w-12 h-12 flex items-center justify-center mx-auto mb-4">
                <i className="ri-trophy-line text-3xl text-amber-400 animate-float"></i>
              </div>
              <div className="text-3xl font-bold text-gradient-gold mb-2">
                {totalReturn.toFixed(1)}%
              </div>
              <div className="text-slate-300">Rendimento Totale</div>
            </Card>

            <Card className="glass-gradient p-6 text-center hover-lift animate-bounce-in delay-200">
              <div className="w-12 h-12 flex items-center justify-center mx-auto mb-4">
                <i className="ri-calendar-line text-3xl text-amber-400 animate-float"></i>
              </div>
              <div className="text-3xl font-bold text-gradient-gold mb-2">
                {activeDays}
              </div>
              <div className="text-slate-300">Giorni Attivi</div>
            </Card>

            <Card className="glass-gradient p-6 text-center hover-lift animate-bounce-in delay-300">
              <div className="w-12 h-12 flex items-center justify-center mx-auto mb-4">
                <i className="ri-line-chart-line text-3xl text-amber-400 animate-float"></i>
              </div>
              <div className="text-3xl font-bold text-gradient-gold mb-2">
                {monthlyAvg.toFixed(2)}%
              </div>
              <div className="text-slate-300">Media Mensile</div>
            </Card>

            <Card className="glass-gradient p-6 text-center hover-lift animate-bounce-in delay-500">
              <div className="w-12 h-12 flex items-center justify-center mx-auto mb-4">
                <i className="ri-shield-check-line text-3xl text-amber-400 animate-float"></i>
              </div>
              <div className="text-3xl font-bold text-gradient-gold mb-2">
                {sharpeRatio.toFixed(2)}
              </div>
              <div className="text-slate-300">Sharpe Ratio</div>
            </Card>
          </div>
        </div>
      </section>

      {/* Selettore periodo */}
      <section className="py-8 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-wrap justify-center gap-4 mb-12 animate-slide-in-up delay-300">
            {[
              { key: 'tutto', label: 'Tutto' },
              { key: 'anno', label: 'Anno' },
              { key: 'semestre', label: 'Semestre' },
              { key: 'trimestre', label: 'Trimestre' },
              { key: 'mese', label: 'Mese' },
              { key: 'settimana', label: 'Settimana' },
              { key: 'giorno', label: 'Giorno' }
            ].map((period) => (
              <button
                key={period.key}
                onClick={() => setSelectedPeriod(period.key)}
                className={`px-6 py-3 rounded-xl font-medium transition-all duration-300 whitespace-nowrap ${
                  selectedPeriod === period.key
                    ? 'bg-gradient-to-r from-amber-400 to-yellow-500 text-slate-900 glow-gold-strong'
                    : 'glass-effect text-slate-300 hover:glass-gradient hover:text-white'
                }`}
              >
                {period.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Performance dettagliate */}
      <section className="py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Metriche di performance */}
            <Card className="glass-gradient p-8 animate-slide-in-left delay-500">
              <h3 className="text-2xl font-bold text-gradient-gold mb-8 flex items-center gap-3">
                <div className="w-8 h-8 flex items-center justify-center">
                  <i className="ri-bar-chart-line text-amber-400"></i>
                </div>
                Performance {selectedPeriod === 'tutto' ? 'Totale' : selectedPeriod.charAt(0).toUpperCase() + selectedPeriod.slice(1)}
              </h3>
              
              <div className="space-y-6">
                <div className="flex justify-between items-center p-4 glass-effect rounded-lg">
                  <span className="text-slate-300">Rendimento</span>
                  <span className="text-2xl font-bold text-gradient-gold">
                    +{typeof currentData.rendimento === 'number' ? currentData.rendimento.toFixed(2) : currentData.rendimento}%
                  </span>
                </div>
                
                <div className="flex justify-between items-center p-4 glass-effect rounded-lg">
                  <span className="text-slate-300">Profitto Massimo</span>
                  <span className="text-xl font-bold text-green-400">
                    {typeof currentData.profitto === 'number' ? currentData.profitto.toFixed(2) : currentData.profitto}%
                  </span>
                </div>
                
                <div className="flex justify-between items-center p-4 glass-effect rounded-lg">
                  <span className="text-slate-300">Max Drawdown</span>
                  <span className="text-xl font-bold text-red-400">
                    -{currentData.drawdown.toFixed(2)}%
                  </span>
                </div>
              </div>
            </Card>

            {/* Analisi del rischio */}
            <Card className="glass-gradient p-8 animate-slide-in-right delay-700">
              <h3 className="text-2xl font-bold text-gradient-gold mb-8 flex items-center gap-3">
                <div className="w-8 h-8 flex items-center justify-center">
                  <i className="ri-shield-line text-amber-400"></i>
                </div>
                Analisi del Rischio
              </h3>
              
              <div className="space-y-6">
                <div className="flex justify-between items-center p-4 glass-effect rounded-lg">
                  <span className="text-slate-300">Sharpe Ratio</span>
                  <span className="text-xl font-bold text-blue-400">
                    {typeof currentData.sharpe === 'number' ? currentData.sharpe.toFixed(2) : currentData.sharpe}
                  </span>
                </div>
                
                <div className="flex justify-between items-center p-4 glass-effect rounded-lg">
                  <span className="text-slate-300">Volatility Ratio</span>
                  <span className="text-xl font-bold text-purple-400">
                    {currentData.volatility.toFixed(2)}
                  </span>
                </div>
                
                <div className="flex justify-between items-center p-4 glass-effect rounded-lg">
                  <span className="text-slate-300">Recovery Factor</span>
                  <span className="text-xl font-bold text-cyan-400">
                    {currentData.recovery.toFixed(2)}
                  </span>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Statistiche Aggiuntive */}
      <section className="py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <Card className="glass-gradient p-8 animate-scale-in delay-500">
            <h3 className="text-2xl font-bold text-gradient-gold mb-8 text-center flex items-center justify-center gap-3">
              <div className="w-8 h-8 flex items-center justify-center">
                <i className="ri-line-chart-fill text-amber-400"></i>
              </div>
              Statistiche Dettagliate
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="text-center p-6 glass-effect rounded-lg">
                <div className="text-3xl font-bold text-amber-400 mb-2">7.95%</div>
                <div className="text-slate-300 text-sm">Media Settimanale</div>
              </div>
              
              <div className="text-center p-6 glass-effect rounded-lg">
                <div className="text-3xl font-bold text-amber-400 mb-2">17.2%</div>
                <div className="text-slate-300 text-sm">Deviazione Giornaliera</div>
              </div>
              
              <div className="text-center p-6 glass-effect rounded-lg">
                <div className="text-3xl font-bold text-amber-400 mb-2">10.43%</div>
                <div className="text-slate-300 text-sm">Deviazione Mensile</div>
              </div>
              
              <div className="text-center p-6 glass-effect rounded-lg">
                <div className="text-3xl font-bold text-amber-400 mb-2">10.55%</div>
                <div className="text-slate-300 text-sm">Deviazione Annuale</div>
              </div>
              
              <div className="text-center p-6 glass-effect rounded-lg">
                <div className="text-3xl font-bold text-amber-400 mb-2">3.86%</div>
                <div className="text-slate-300 text-sm">Rendimento 1D</div>
              </div>
              
              <div className="text-center p-6 glass-effect rounded-lg">
                <div className="text-3xl font-bold text-amber-400 mb-2">#9</div>
                <div className="text-slate-300 text-sm">Ranking Globale</div>
              </div>
            </div>
          </Card>
        </div>
      </section>

      {/* Account Info */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <Card className="glass-gradient p-8 animate-scale-in delay-700">
            <h3 className="text-3xl font-bold text-gradient-gold mb-6">
              Account TCA BOMBER PUSHED
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div className="text-center">
                <div className="text-2xl font-bold text-amber-400 mb-2">#9</div>
                <div className="text-slate-300">Ranking Globale</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-amber-400 mb-2">3.86%</div>
                <div className="text-slate-300">Rendimento Giornaliero</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-amber-400 mb-2">10.4%</div>
                <div className="text-slate-300">Media Settimanale</div>
              </div>
            </div>
            
            {/* Link al grafico completo */}
            <div className="text-center animate-slide-in-up delay-700 px-4">
              <a 
                href="https://socialratings.fpglobaltrading.com/widgets/ratings/6246?widgetKey=social_platform_ratings"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-3 px-4 sm:px-6 md:px-8 py-4 bg-gradient-to-r from-amber-400 to-yellow-500 text-slate-900 font-bold rounded-xl hover:from-amber-500 hover:to-yellow-600 transition-all duration-300 glow-gold hover-lift text-sm sm:text-base max-w-full"
              >
                <div className="w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center flex-shrink-0">
                  <i className="ri-external-link-line text-sm sm:text-lg"></i>
                </div>
                <span className="truncate">Visualizza Grafico Completo</span>
              </a>
            </div>
          </Card>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="animate-slide-in-up delay-700">
            <h2 className="text-4xl md:text-5xl font-bold text-gradient-gold mb-6">
              Vuoi Risultati Come Questi?
            </h2>
            <p className="text-xl text-slate-300 mb-12 max-w-2xl mx-auto">
              Unisciti alla Fortal Society e accedi alle stesse strategie che hanno generato questi risultati verificati
            </p>
            
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <button
                onClick={() => navigate('/servizi')}
                className="px-8 py-4 bg-gradient-to-r from-amber-400 to-yellow-500 text-slate-900 font-bold rounded-xl hover:from-amber-500 hover:to-yellow-600 transition-all duration-300 glow-gold hover-lift whitespace-nowrap"
              >
                Scopri i Nostri Servizi
              </button>
              
              <button
                onClick={() => navigate('/corsi')}
                className="px-8 py-4 glass-effect text-slate-200 font-bold rounded-xl hover:glass-gradient hover:text-white transition-all duration-300 hover-lift whitespace-nowrap"
              >
                Inizia la Formazione
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
